import React from 'react';
import { Shield, Lock, Eye, Clock, Mail, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';

const PrivacyPage = () => {
  return (
    <div>
      <SEOHelmet 
        title="Privacy Policy | Legal Document Specialist"
        description="Privacy policy for Legal Document Specialist's Payment Applicator and services. Learn how we protect and handle your information."
        keywords="privacy policy, data protection, legal document services, confidentiality"
        canonical="/privacy"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'var(--brand-primary)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem',
              color: 'white'
            }}>
              <Shield size={40} />
            </div>
            
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)',
              fontSize: 'clamp(2rem, 4vw, 3rem)'
            }}>
              Privacy Policy
            </h1>
            
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.8'
            }}>
              Legal Document Specialist, Inc. ("LDS"), 225 South Civic Drive, Suite 2-16, Palm Springs, CA 92262. (760) 866-9182. support@legaldocspecialist.com.
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '3rem'
            }}>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                This page covers the LDS Payment Applicator, an internal tool that matches check-stub invoice numbers to invoices in our QuickBooks Online company and, after a person approves, records the payment.
              </p>
            </div>

            {/* What We Collect */}
            <div style={{ marginBottom: '3rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#EFF6FF',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Eye size={24} color="var(--brand-primary)" />
                </div>
                <h2 className="heading-2">What we collect</h2>
              </div>
              <div style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)'
              }}>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  The app reads invoice numbers, amounts, and check or remittance IDs from stubs you upload or paste. It uses our Intuit/QuickBooks connection to look up those invoices and post approved payments. It does not sell this data.
                </p>
              </div>
            </div>

            {/* Who Can See It */}
            <div style={{ marginBottom: '3rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#FEF3C7',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={24} color="#F59E0B" />
                </div>
                <h2 className="heading-2">Who can see it</h2>
              </div>
              <div style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)'
              }}>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  LDS staff who operate the tool, and Intuit/QuickBooks to the extent needed to look up invoices and record payments. We do not use the connection to contact your customers.
                </p>
              </div>
            </div>

            {/* How Long We Keep It */}
            <div style={{ marginBottom: '3rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#F0FDF4',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Clock size={24} color="#10B981" />
                </div>
                <h2 className="heading-2">How long we keep it</h2>
              </div>
              <div style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)'
              }}>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  Stub files and preview records stay only as long as needed to apply or review the payment, then follow our normal office retention for accounting records.
                </p>
              </div>
            </div>

            {/* Questions */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#EFF6FF',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Mail size={24} color="var(--brand-primary)" />
                </div>
                <h2 className="heading-2">Questions</h2>
              </div>
              <div style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)'
              }}>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '1.5rem' }}>
                  Email support@legaldocspecialist.com.
                </p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <a 
                    href="mailto:support@legaldocspecialist.com"
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'var(--brand-primary)',
                      color: 'white',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Mail size={18} />
                    Email Us
                  </a>
                  <a href="/quote"
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'transparent',
                      color: 'var(--brand-primary)',
                      border: '2px solid var(--brand-primary)',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    Request a Quote
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '60px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{
              backgroundColor: '#FEF3C7',
              border: '2px solid #F59E0B',
              borderRadius: '12px',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <p className="body-small" style={{ color: '#78350F', fontStyle: 'italic' }}>
                This is an operational notice, not legal advice. Last updated August 23, 2026.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
